import { useState } from "react";
import { portfolioItems as staticPortfolio } from "../lib/siteConfig";
import { usePublicData } from "../hooks/usePublicData";
import { useLanguage } from "../context/LanguageContext";
import CTAStrip from "../components/CTAStrip";
import EmptyState from "../components/ui/EmptyState";
import { pub } from "../lib/pubThemeClasses";
import { API_BASE } from "../lib/env";
import { pickPortfolioTitle } from "../lib/cmsI18n";

export default function Portfolio() {
  const { portfolio } = usePublicData();
  const { t, language } = useLanguage();
  const all = portfolio?.length ? portfolio : staticPortfolio;
  const uniq = [...new Set(all.map((p) => p.category))];

  const [activeCat, setActiveCat] = useState(null);
  const items = activeCat === null ? all : all.filter((p) => p.category === activeCat);

  const resolveImg = (p) => (p.storage_path ? `${API_BASE}/public/media/${p.storage_path}` : p.image);

  return (
    <div>
      <section className={`relative border-b bg-grain ${pub.borderSubtle}`}>
        <div className="absolute inset-0 grid-lines opacity-50 pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF4F00] mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-[#FF4F00]" /> {t("portfolio.eyebrow")}
          </div>
          <h1 className={`font-display text-5xl sm:text-6xl md:text-7xl font-black uppercase tracking-tight leading-[0.9] max-w-4xl ${pub.heroHeading}`}>
            {t("portfolio.hero.line1")}
            <br />
            <span className="text-[#FF4F00]">{t("portfolio.hero.highlight")}</span>
          </h1>
          <p className={`mt-6 text-lg max-w-2xl ${pub.muted}`}>{t("portfolio.hero.sub")}</p>
        </div>
      </section>

      <section className={`border-b ${pub.borderSubtle}`}>
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-12 md:py-16">
          <div className="flex flex-wrap gap-2 mb-10" data-testid="portfolio-filters">
            <button
              type="button"
              onClick={() => setActiveCat(null)}
              data-testid="portfolio-filter-all"
              className={`px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] border transition-colors ${
                activeCat === null
                  ? "border-[#FF4F00] bg-[#FF4F00] text-black"
                  : `border text-neutral-600 hover:border-orange-400/55 dark:text-zinc-400 ${pub.borderStrongTone} ${pub.hoverText}`
              }`}
            >
              {t("portfolio.filter.all")}
            </button>
            {uniq.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setActiveCat(c)}
                data-testid={`portfolio-filter-${c.replace(/\s|\/|\+/g, "-").toLowerCase()}`}
                className={`px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] border transition-colors ${
                  activeCat === c
                    ? "border-[#FF4F00] bg-[#FF4F00] text-black"
                    : `border text-neutral-600 hover:border-orange-400/55 dark:text-zinc-400 ${pub.borderStrongTone} ${pub.hoverText}`
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {items.length === 0 && all.length === 0 ? (
            <EmptyState
              title={t("portfolio.empty.resultsTitle")}
              description={t("portfolio.cmsEmpty")}
              actionLabel={t("materials.empty.home")}
              actionTo="/"
            />
          ) : items.length === 0 ? (
            <EmptyState description={t("portfolio.empty.none")} onAction={() => setActiveCat(null)} actionLabel={t("portfolio.filter.all")} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((p) => {
                const pTitle = pickPortfolioTitle(p, language);
                return (
                <div key={p.id || p.title} className={`relative group overflow-hidden border ${pub.borderSubtle}`}>
                  <img src={resolveImg(p)} alt={pTitle} className="w-full h-[340px] object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/20 to-transparent" />
                  <div className="absolute bottom-5 left-5 right-5">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4F00] mb-1">{p.category}</div>
                    <div className="font-display text-xl font-bold uppercase tracking-tight text-white drop-shadow-sm">{pTitle}</div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <CTAStrip />
    </div>
  );
}
